// src/pages/Home/HeaderSlider.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowAltCircleDown,
  FaArrowRight,
  FaClock,
  FaLayerGroup,
  FaUserPlus,
} from "react-icons/fa";

import { getSettings } from "../../services/settingsServices";
import { toAbsolutePhotoUrl } from "../../services/settingsPhotoServices";
import { useUser } from "./UserContext";

import "./HeaderSlider.css";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const FALLBACK_1 = "/img/no-image.jpg";
const FALLBACK_2 = "/img/no-image.jpg";

const getNavbarOffset = () => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue("--site-navbar-h")
    .trim();

  const parsed = parseInt(value, 10);

  return Number.isFinite(parsed) ? parsed + 20 : 130;
};

const HeaderSlider = ({ user: userProp }) => {
  const navigate = useNavigate();
  const { user: userFromContext } = useUser();

  const user = userProp ?? userFromContext;

  const isLogged = useMemo(() => {
    if (!user) return false;

    if (typeof user === "object") {
      return Boolean(
        user.id ||
          user.userId ||
          user.email ||
          user.token ||
          localStorage.getItem("token")
      );
    }

    return true;
  }, [user]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const settings = await getSettings();
        const s = Array.isArray(settings) ? settings[0] : settings;

        if (!mounted) return;

        const headers = [];

        const src1 = toAbsolutePhotoUrl(
          (s?.header_url1 || s?.header1_photo_url || "").trim()
        );

        const src2 = toAbsolutePhotoUrl(
          (s?.header_url2 || s?.header2_photo_url || "").trim()
        );

        if (src1) {
          headers.push({
            src: src1,
            text:
              s?.header_texte1 ||
              s?.header_title ||
              "Bienvenue sur le site du club",
            align: "left",
            fallback: FALLBACK_1,
          });
        }

        if (src2) {
          headers.push({
            src: src2,
            text:
              s?.header_texte2 ||
              s?.header_subtitle ||
              "Retrouvez les informations, horaires et actualités du club.",
            align: "right",
            fallback: FALLBACK_2,
          });
        }

        if (!headers.length) {
          headers.push({
            src: FALLBACK_1,
            text:
              s?.header_texte1 ||
              s?.header_title ||
              "Bienvenue sur le site du club",
            align: "left",
            fallback: FALLBACK_1,
          });

          headers.push({
            src: FALLBACK_2,
            text:
              s?.header_texte2 ||
              s?.header_subtitle ||
              "Retrouvez les informations, horaires et actualités du club.",
            align: "right",
            fallback: FALLBACK_2,
          });
        }

        const primaryColor = s?.couleur_principale || s?.primary_color;

        if (primaryColor) {
          document.documentElement.style.setProperty(
            "--primary-color",
            primaryColor
          );
        }

        setSlides(headers);
        setCurrentIndex(0);
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);

        if (!mounted) return;

        setSlides([
          {
            src: FALLBACK_1,
            text: "Bienvenue sur le site du club",
            align: "left",
            fallback: FALLBACK_1,
          },
          {
            src: FALLBACK_2,
            text: "Retrouvez les informations, horaires et actualités du club.",
            align: "right",
            fallback: FALLBACK_2,
          },
        ]);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const timeout = setTimeout(() => {
      setCurrentIndex((previous) => (previous + 1) % slides.length);
    }, 8000);

    return () => clearTimeout(timeout);
  }, [currentIndex, slides]);

  const scrollToId = (id) => {
    const element = document.getElementById(id);

    if (!element) return;

    const offset = getNavbarOffset();
    const y = element.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  };

  const handlePrimaryAction = () => {
    if (isLogged) {
      navigate("/ajouter-inscription-adherent");
      return;
    }

    navigate("/register");
  };

  if (!slides.length) {
    return (
      <div className="header-hero-skeleton">
        <div className="header-skeleton-inner">Chargement des visuels...</div>
      </div>
    );
  }

  const activeSlide = slides[currentIndex];

  return (
    <header className="header-hero" data-align={activeSlide.align}>
      {slides.map((slide, index) => (
        <div
          key={index}
          className={cn(
            "header-slide",
            index === currentIndex ? "active" : "inactive"
          )}
          aria-hidden={index !== currentIndex}
        >
          {slide.src && (
            <img
              src={slide.src}
              alt={`Visuel ${index + 1}`}
              className="header-slide-image"
              loading={index === 0 ? "eager" : "lazy"}
              onError={(event) => {
                event.currentTarget.src =
                  slide.fallback || (index === 0 ? FALLBACK_1 : FALLBACK_2);
              }}
            />
          )}

          <div
            className={cn(
              "header-slide-overlay",
              slide.align === "right" ? "overlay-right" : "overlay-left"
            )}
          />
        </div>
      ))}

      <div className="header-content">
        <div
          className={cn(
            "header-content-inner",
            activeSlide.align === "right"
              ? "header-content-right"
              : "header-content-left"
          )}
        >
          <div
            className="header-text"
            dangerouslySetInnerHTML={{
              __html: activeSlide.text,
            }}
          />

          <div className="header-actions">
            <button
              type="button"
              onClick={handlePrimaryAction}
              className="header-primary-button"
            >
              <FaUserPlus />
              {isLogged ? "Faire une inscription" : "Créer un compte"}
              <FaArrowRight className="header-button-arrow" />
            </button>

            <button
              type="button"
              onClick={() => scrollToId("disciplines")}
              className="header-secondary-button"
            >
              <FaLayerGroup />
              Disciplines
            </button>

            <button
              type="button"
              onClick={() => scrollToId("cours")}
              className="header-secondary-button"
            >
              <FaClock />
              Groupes & horaires
            </button>
          </div>
        </div>

        {slides.length > 1 && (
          <div className="header-dots">
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  "header-dot",
                  index === currentIndex ? "active" : ""
                )}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Aller au visuel ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => scrollToId("cours")}
        className="header-scroll"
        aria-label="Descendre"
      >
        <FaArrowAltCircleDown size={40} />
      </button>
    </header>
  );
};

export default HeaderSlider;